import { forwardRef, useState, type ComponentPropsWithoutRef } from 'react'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn, formatShortDate } from '@/lib/utils'
import { CalendarIcon, XIcon } from 'lucide-react'
import {
  startOfDay,
  addDays,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  format,
  isSameDay,
  isAfter,
  isBefore,
} from 'date-fns'
import { useMemo } from 'react'
import { useSearchFilters } from './useSearchFilters'

function DateFilterCalendarGrid() {
  const { localFilters, updateLocalFilters } = useSearchFilters()

  const today = useMemo(() => new Date(), [])
  const firstDay = today
  const lastDay = useMemo(() => addDays(today, 14), [today])

  const calendarDates = useMemo(() => {
    const start = startOfWeek(firstDay, { weekStartsOn: 1 })
    const end = endOfWeek(lastDay, { weekStartsOn: 1 })
    return eachDayOfInterval({ start, end })
  }, [firstDay, lastDay])

  const handleDateClick = (date: Date) => {
    const fromDate = localFilters.fromDate ? startOfDay(localFilters.fromDate) : undefined
    const toDate = localFilters.toDate ? startOfDay(localFilters.toDate) : undefined
    const clicked = startOfDay(date)

    // No selection yet → set as fromDate
    if (!fromDate) {
      updateLocalFilters({ fromDate: date, toDate: undefined })
      return
    }

    // Only fromDate selected
    if (!toDate) {
      if (isSameDay(clicked, fromDate)) {
        updateLocalFilters({ fromDate: undefined, toDate: undefined })
        return
      }
      if (isAfter(clicked, fromDate)) {
        updateLocalFilters({ toDate: date })
        return
      }
      // Clicked before fromDate → restart with this date as fromDate
      updateLocalFilters({ fromDate: date, toDate: undefined })
      return
    }

    // Range already exists → restart selection from this date
    updateLocalFilters({ fromDate: date, toDate: undefined })
  }

  return (
    <div>
      <div className="mb-2 grid grid-cols-7 gap-0.5">
        {['Po', 'Út', 'St', 'Čt', 'Pá', 'So', 'Ne'].map((day) => (
          <div key={day} className="text-center text-xs font-medium tracking-wider text-slate-500 uppercase">
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-0.5">
        {calendarDates.map((date, index) => {
          const dayNumber = format(date, 'd.')
          const monthNumber = format(date, 'M.')
          const isOutsideRange = date < startOfDay(today) || date > startOfDay(lastDay)
          const isFrom = !!localFilters.fromDate && isSameDay(date, localFilters.fromDate)
          const isTo = !!localFilters.toDate && isSameDay(date, localFilters.toDate)
          const isInRange =
            !!localFilters.fromDate &&
            !!localFilters.toDate &&
            isAfter(date, startOfDay(localFilters.fromDate)) &&
            isBefore(date, startOfDay(localFilters.toDate))
          const isSelected = isFrom || isTo
          const isClickable = !isOutsideRange

          return (
            <Button
              key={index}
              variant="ghost"
              disabled={!isClickable}
              className={cn(
                'flex h-10 w-full items-baseline gap-0 rounded-md px-0 text-base font-medium',
                isSelected && 'bg-green-700 text-white hover:bg-green-700 hover:text-white',
                !isSelected && isInRange && 'bg-green-100 text-green-900 hover:bg-green-100',
                !isSelected && !isInRange && isClickable && 'text-slate-900 hover:bg-slate-100',
                !isClickable && 'cursor-not-allowed text-slate-300 opacity-100!',
              )}
              onClick={() => handleDateClick(date)}
            >
              {dayNumber} <span className="text-xs opacity-50">{monthNumber}</span>
            </Button>
          )
        })}
      </div>
    </div>
  )
}

function formatDateTriggerLabel(fromDate: Date | undefined, toDate: Date | undefined): string {
  if (fromDate && toDate) {
    return `${formatShortDate(fromDate)} – ${formatShortDate(toDate)}`
  }
  if (fromDate) {
    return formatShortDate(fromDate)
  }
  if (toDate) {
    return `do ${formatShortDate(toDate)}`
  }
  return 'Libovolný termín'
}

export function DateFilterMobile() {
  const { localFilters, updateLocalFilters } = useSearchFilters()
  const hasSelection = !!localFilters.fromDate || !!localFilters.toDate

  return (
    <div className="flex flex-col gap-0">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-slate-800">Termín pobytu</span>
        {hasSelection && (
          <Button
            variant="ghost"
            size="sm"
            className="text-slate-500"
            onClick={() => updateLocalFilters({ fromDate: undefined, toDate: undefined })}
          >
            <XIcon className="h-4 w-4" />
            Zrušit
          </Button>
        )}
      </div>
      <div className="pt-5">
        <DateFilterCalendarGrid />
      </div>
    </div>
  )
}

const DateFilterTrigger = forwardRef<HTMLButtonElement, ComponentPropsWithoutRef<typeof Button>>(
  function DateFilterTrigger(props, ref) {
    const { localFilters } = useSearchFilters()

    return (
      <Button
        ref={ref}
        variant="ghost"
        className="justify-start border-0 bg-slate-100 text-left text-sm! font-normal focus-visible:bg-slate-100 focus-visible:ring-1 focus-visible:ring-slate-200"
        {...props}
      >
        <CalendarIcon className="text-red h-4 w-4" />
        {formatDateTriggerLabel(localFilters.fromDate, localFilters.toDate)}
      </Button>
    )
  },
)

export function DateFilterDesktop() {
  const { localFilters, updateLocalFilters, commitFilters } = useSearchFilters()
  const [open, setOpen] = useState(false)
  const hasSelection = !!localFilters.fromDate || !!localFilters.toDate

  const handleClose = () => {
    commitFilters()
    setOpen(false)
  }

  return (
    <div className="flex grow basis-0 flex-col gap-1">
      <label htmlFor="fromDate" className="text-sm text-slate-400">
        Termín pobytu
      </label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <DateFilterTrigger />
        </PopoverTrigger>
        <PopoverContent className="w-88" onInteractOutside={handleClose}>
          <div className="mb-4 flex items-start justify-between gap-2">
            <div>
              <p className="text-base font-medium text-slate-800">Výběr termínu</p>
              <p className="text-sm text-slate-500">Vyberte den příjezdu, případně i den odjezdu</p>
            </div>
            {hasSelection && (
              <Button
                variant="ghost"
                size="sm"
                className="text-slate-500"
                onClick={() => updateLocalFilters({ fromDate: undefined, toDate: undefined })}
              >
                <XIcon className="h-4 w-4" />
                Zrušit
              </Button>
            )}
          </div>
          <DateFilterCalendarGrid />
          <div className="mt-4 flex justify-end">
            <Button size="sm" onClick={handleClose}>
              Potvrdit
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}
